import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { black, gray4, gray6, primary, success, white, hexToRgbaString } from 'styles/global_defaults/colors';
import { boldFontWeight, textLargeFontSize } from 'styles/global_defaults/fonts';
import { dropdownZIndex, halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/pendo.config.json';

type ContactBarProps = {
  label: string;
  count: number;
  onCopyEmails: () => void | Promise<void>;
  onContactLearners: () => void;
};

type CopyState = 'idle' | 'loading' | 'success';

const SUCCESS_DISPLAY_MS = 1500;

const styles = css`
  position: absolute;
  bottom: ${standardSpacing}px;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${dropdownZIndex};

  padding: ${halfSpacing}px;
  background: ${white};
  border: 1px solid ${gray6};
  border-radius: 100px;
  box-shadow: 0 ${halfSpacing}px ${standardSpacing}px 0 ${hexToRgbaString(black, 0.2)};

  .contact-bar-label {
    padding: ${halfSpacing}px ${standardSpacing}px;
    color: ${black};
    font-size: ${textLargeFontSize}px;
    font-weight: ${boldFontWeight};
  }

  .contact-bar-action {
    gap: ${quarterSpacing}px;
    padding: ${halfSpacing}px ${standardSpacing}px;
    color: ${primary};

    .icon {
      color: ${primary};
    }

    &[aria-disabled='true'],
    &.is-loading {
      color: ${gray4};

      .icon {
        color: ${gray4};
      }
    }

    &.is-success {
      color: ${success};

      .icon {
        color: ${success};
      }
    }
  }
`;

const copyStateClass: Record<CopyState, string> = {
  idle: '',
  loading: 'is-loading',
  success: 'is-success',
};

const ContactBar = ({ label, count, onCopyEmails, onContactLearners }: ContactBarProps) => {
  const dataQa = config.pendo.learnerProgress.contactBar;
  const isDisabled = count === 0;
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    if (copyState !== 'success') return undefined;
    const timeoutId = window.setTimeout(() => setCopyState('idle'), SUCCESS_DISPLAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [copyState]);

  const handleCopyEmails = () => {
    if (isDisabled || copyState !== 'idle') return;
    setCopyState('loading');
    Promise.resolve(onCopyEmails())
      .then(() => setCopyState('success'))
      .catch(() => setCopyState('idle'));
  };

  const copyLabel = {
    idle: t.LEARNER_PROGRESS.CONTACT_BAR.COPY_ALL_EMAILS(),
    loading: t.LEARNER_PROGRESS.CONTACT_BAR.REQUESTING(),
    success: t.LEARNER_PROGRESS.CONTACT_BAR.COPIED_ALL_EMAILS(),
  }[copyState];

  return (
    <div css={styles} className='d-flex align-items-center'>
      <div className='contact-bar-label text-nowrap'>
        {t.LEARNER_PROGRESS.CONTACT_BAR.LABEL(count, label)}
      </div>
      <ClickableContainer
        className={`contact-bar-action d-flex align-items-center text-nowrap ${copyStateClass[copyState]}`}
        onClick={handleCopyEmails}
        disabled={isDisabled || copyState !== 'idle'}
        aria-busy={copyState === 'loading'}
        data-qa={dataQa.copyAllEmails}
      >
        <NvIcon size='small' icon={copyState === 'success' ? 'success' : 'duplicate'} />
        <span>{copyLabel}</span>
      </ClickableContainer>
      <ClickableContainer
        className='contact-bar-action d-flex align-items-center text-nowrap'
        onClick={onContactLearners}
        disabled={isDisabled}
        data-qa={dataQa.contactLearners}
      >
        <NvIcon size='small' icon='send-message' />
        <span>{t.LEARNER_PROGRESS.CONTACT_BAR.CONTACT_LEARNERS()}</span>
      </ClickableContainer>
    </div>
  );
};

export default ContactBar;
